import { OpalApiResponseDocument } from '@opallabs/api'
import { CATEGORY_KINDS, CategoryKinds } from '@opallabs/concepts'
import { factories } from '@opallabs/starman'
import { PlanResponse } from '../types'

type InsertCategoryParams = {
  id?: string
  kind: CategoryKinds
  position: number
  title?: string | null
}
type CategoriesById = PlanResponse['data']['categories']['by_id']
type CategoryDef = CategoriesById[string]

type CategoryHierarchy = OpalApiResponseDocument<
  'ExperimentalReadPlanBFFV1',
  200
>['data']['categories']['hierarchy']

function buildFactoryCategoryParams(params: InsertCategoryParams) {
  if (params.kind === CATEGORY_KINDS.smart_swimlane && params.title === null) {
    throw new Error('Title is required when category is a Smart Swimlane')
  }

  return {
    // passing id: undefined would explicitly set the id property to `undefined` instead of
    // falling back to the default value
    ...(params.id ? { id: params.id } : undefined),
    kind: params.kind,
    title: params.title,
    position: params.position,
  } satisfies Parameters<
    typeof factories.asgardBff.components.normalizedCategory.build
  >[0]
}

export const createPlaceholderCategory = (params: InsertCategoryParams) =>
  factories.asgardBff.components.normalizedCategory.build(
    buildFactoryCategoryParams(params)
  ) satisfies CategoryDef

export const insertCategory = (
  newCategory: CategoryDef,
  ancestorCategoryIds?: Array<string>
) => {
  return (draftResponse: PlanResponse) => {
    draftResponse.data.categories.by_id[newCategory.id] = newCategory

    insertCategoryAtPosition(
      draftResponse.data.categories.hierarchy,
      ancestorCategoryIds ?? [],
      draftResponse.data.categories.by_id,
      newCategory
    )

    return draftResponse
  }
}
/**
 * Walk the category hierarchy and find the correct position to insert the new category.
 * Note that this is a function used inside an immer producer, so the goal is to mutate
 * the hierarchy array instead of doing more typical functional methods.
 */
function insertCategoryAtPosition(
  hierarchyLevel: CategoryHierarchy,
  ancestorCategoryIds: Array<string>,
  categoriesById: CategoriesById,
  newCategory: CategoryDef
) {
  // If there aren't any ancestor categories, we need to insert the new category at this level in the hierarchy.
  // We'll check sibling category positions to find the right index for it.
  if (ancestorCategoryIds.length === 0) {
    for (let index = 0; index <= hierarchyLevel.length; index++) {
      const indexCategoryPosition =
        categoriesById[hierarchyLevel[index]?.id]?.position ?? -1
      const shouldInsertBeforeIndexCategory =
        newCategory.position <= indexCategoryPosition

      // If the checked category has a position larger than us, we can insert at this point
      if (shouldInsertBeforeIndexCategory) {
        hierarchyLevel.splice(index, 0, { id: newCategory.id, children: [] })
        break
      }

      // If we haven't inserted the category, but we're at the last index, we must be
      // inserting into the last position
      const shouldInsertAsLast = index === hierarchyLevel.length
      if (shouldInsertAsLast) {
        hierarchyLevel.push({ id: newCategory.id, children: [] })
        break
      }
    }
  }
  // If there are ancestor categories, we need to find the next one and continue the recursive insert with its children
  else {
    const nextAncestorId = ancestorCategoryIds.shift()
    const ancestor = hierarchyLevel.find(({ id }) => id === nextAncestorId)

    if (!ancestor) {
      console.error(
        `Unable to place new category in hierarchy, ancestor category ${nextAncestorId} not found.`
      )
      return
    }

    // Need to cast `ancestor.children` to CategoryHierarchy because our
    // documentation can't currently process recursive types
    insertCategoryAtPosition(
      ancestor.children as CategoryHierarchy,
      ancestorCategoryIds,
      categoriesById,
      newCategory
    )
  }
}
